/**
 * Une holding au-dessus de votre société : ce qu'elle vous fait gagner, et
 * quand elle ne vous fait rien gagner du tout.
 *
 * LA HOLDING NE BAISSE PAS L'IMPÔT DE CE QUE VOUS VOUS VERSEZ. Tout euro qui
 * finit dans votre poche paie, avec ou sans holding, l'impôt sur le revenu
 * et les prélèvements sociaux, ou vos cotisations. Ce qu'elle change, c'est
 * l'argent qui NE vous est PAS versé : les dividendes qui remontent de votre
 * société à la holding ne paient presque rien (régime mère-fille), et la
 * holding peut les réinvestir. Elle fait donc payer plus tard, comme l'impôt
 * sur les sociétés, et seulement pour l'argent que vous ne consommez pas.
 *
 * TROIS CAS, TROIS RÉPONSES :
 *   - vous voulez vous verser l'argent pour vivre : la holding ne change
 *     rien, et coûte une seconde comptabilité ;
 *   - vous voulez réinvestir vos bénéfices : la holding reçoit les
 *     dividendes presque sans impôt, là où vous paieriez 31,4 % ;
 *   - vous voulez revendre votre société un jour : la holding paie un impôt
 *     sur une petite part de la plus-value, là où vous paieriez 31,4 % du
 *     gain, à condition de détenir les titres depuis au moins deux ans.
 *
 * EN EURL, DEUX LECTURES, DONC UNE FOURCHETTE. Lue à la lettre, la règle des
 * 10 % (CSS, art. L. 136-3, II, 2°) ne vise que les dividendes perçus par le
 * gérant, son conjoint et ses enfants : ceux que l'EURL verse à la holding y
 * échappent. Mais la Cour de cassation les a réintégrés dans les cotisations
 * du gérant (2e civ., 19 octobre 2023, n° 21-20.366, publié), pour un
 * praticien seul dans sa société. Le moteur calcule les deux, et l'écran
 * montre le plus prudent comme un plancher.
 *
 * Les taux sont relevés dans docs/glossaries/micro-entreprise/company-surveys-2026
 * (holding-tax.md, holding-social.md) ; npm run check:holding les vérifie.
 */

import { cotisationsIndependant, impotSocietes, PFU_IR, PFU_SOCIAL } from "./businessStructure.js";
import { SEUIL_DIVIDENDES_EURL } from "./salaryDividends.js";

const positif = (v) => Math.max(0, v);

/** Régime mère-fille : quote-part de frais et charges réintégrée (CGI, art. 216). */
export const QUOTE_PART_MERE_FILLE = 0.05;
/** Quote-part réduite des sociétés d'un groupe fiscalement intégré. */
export const QUOTE_PART_INTEGRATION = 0.01;
/** Plus-values sur titres de participation : quote-part de 12 % (art. 219, I, a quinquies). */
export const QUOTE_PART_PLUS_VALUE = 0.12;
/** Détention minimale des titres, pour les deux régimes, en années. */
export const DETENTION_MINIMALE_ANS = 2;
/**
 * Apport-cession (CGI, art. 150-0 B ter, version du 21 février 2026) : si la
 * holding revend dans les trois ans de l'apport, le report ne tient que si
 * elle réinvestit 70 % du prix, dans les trois ans, dans une activité
 * économique ; la gestion d'un patrimoine, immobilier ou placements, est
 * exclue.
 */
export const APPORT_CESSION = { delaiAns: 3, quota: 0.7, delaiReinvestissementAns: 3, conservationAns: 5 };

/**
 * Ce qu'il vous reste d'un dividende versé à vous, personne physique, au
 * prélèvement forfaitaire unique.
 *
 * En SASU : 12,8 % d'impôt et 18,6 % de prélèvements sociaux. En EURL, la
 * part qui dépasse 10 % du capital paie les cotisations des indépendants à la
 * place des prélèvements sociaux, et elles se calculent en plus de celles de
 * votre rémunération : c'est la différence entre les deux barèmes.
 */
export function dividendeNetPersonne({ forme, dividendes, capital = 1000, enveloppeRemuneration = 0, activite = "services", artisan = false }) {
    const d = positif(dividendes);
    if (forme === "sasu") {
        return {
            net: d * (1 - PFU_IR - PFU_SOCIAL),
            impot: d * PFU_IR,
            social: d * PFU_SOCIAL,
            cotisations: 0,
            baseSociale: d,
            baseCotisations: 0,
        };
    }
    const nonCotisant = Math.min(d, SEUIL_DIVIDENDES_EURL * capital);
    const cotisant = d - nonCotisant;
    const avant = cotisationsIndependant({ revenuAvantCotisations: enveloppeRemuneration, activite, artisan }).total;
    const apres = cotisationsIndependant({ revenuAvantCotisations: enveloppeRemuneration + cotisant, activite, artisan }).total;
    const cotisations = positif(apres - avant);
    const impot = d * PFU_IR;
    const social = nonCotisant * PFU_SOCIAL;
    // Les deux bases se lisent à l'écran : sans elles, « 18,6 % » à côté de
    // 19 € ressemble à une erreur de calcul.
    return {
        net: d - impot - social - cotisations,
        impot,
        social,
        cotisations,
        baseSociale: nonCotisant,
        baseCotisations: cotisant,
    };
}

/**
 * Ce qu'il vous reste quand la holding vous reverse son argent : un dividende
 * comme un autre, au prélèvement forfaitaire de 31,4 %. On suppose une
 * holding SASU : gérant d'une holding EURL, vous pourriez devoir des
 * cotisations au-delà de 10 % de son capital, et aucune source ne le tranche.
 */
export function sortirDeLaHolding(montant) {
    return positif(montant) * (1 - PFU_IR - PFU_SOCIAL);
}

/**
 * Ce que coûte à la holding un dividende reçu de sa filiale : l'impôt sur la
 * seule quote-part réintégrée, au taux de la holding.
 */
export function impotHolding(dividendes, { integration = false } = {}) {
    const quotePart = positif(dividendes) * (integration ? QUOTE_PART_INTEGRATION : QUOTE_PART_MERE_FILLE);
    return { quotePart, impot: impotSocietes(quotePart) };
}

/**
 * Ce que l'intégration fiscale change à l'impôt des deux sociétés.
 *
 * INTÉGRÉES, ELLES NE PAIENT PLUS QU'UN IMPÔT, sur leurs résultats
 * additionnés, avec un seul plafond de 42 500 € au taux de 15 %. La
 * quote-part des dividendes tombe à 1 %, mais seulement à partir du deuxième
 * exercice dans le groupe. Comparer l'impôt de la holding seule dirait
 * faux dès que la filiale dépasse ce plafond : c'est l'impôt du groupe qui
 * se compare.
 */
export function integrationFiscale({ benefice, dividendes }) {
    const separe = impotSocietes(positif(benefice)) + impotHolding(dividendes).impot;
    const integre = impotSocietes(positif(benefice) + positif(dividendes) * QUOTE_PART_INTEGRATION);
    return { separe, integre, economie: separe - integre };
}

/**
 * Réinvestir le bénéfice que vous ne vous versez pas.
 *
 * @param benefice  ce que votre société gagne en plus de votre rémunération,
 *                  avant l'impôt sur les sociétés
 */
export function reinvestir({
    forme,
    benefice,
    capital = 1000,
    enveloppeRemuneration = 0,
    activite = "services",
    artisan = false,
    integration = false,
}) {
    // Avec ou sans holding, la société paie d'abord son impôt sur les sociétés.
    const impotSociete = impotSocietes(positif(benefice));
    const dividendes = positif(benefice) - impotSociete;

    const sans = dividendeNetPersonne({ forme, dividendes, capital, enveloppeRemuneration, activite, artisan });
    const holding = impotHolding(dividendes, { integration });
    const avecNet = dividendes - holding.impot;
    // Si l'Urssaf suit l'arrêt de 2023, vous devez vous-même les cotisations
    // que ces dividendes auraient payées en arrivant chez vous, sans avoir
    // touché un euro. En SASU, il n'y en a aucune.
    const cotisationsReintegrees = forme === "eurl" ? sans.cotisations : 0;

    // Vous voulez vivre de cet argent : la holding vous le reverse.
    const verse = sortirDeLaHolding(avecNet);

    return {
        impotSociete,
        dividendes,
        sans,
        avec: { net: avecNet, impot: holding.impot, quotePart: holding.quotePart },
        cotisationsReintegrees,
        // Ce que la holding garde de plus que vous, selon le texte, puis
        // selon l'arrêt.
        gain: avecNet - sans.net,
        gainPrudent: avecNet - cotisationsReintegrees - sans.net,
        vivre: {
            net: verse,
            ecart: verse - sans.net,
            ecartPrudent: verse - cotisationsReintegrees - sans.net,
        },
    };
}

/**
 * Revendre votre société.
 *
 * DEUX HOLDINGS QUI NE SE RESSEMBLENT PAS. Créée avant la société, la
 * holding porte toute la plus-value. Créée aujourd'hui en y apportant vos
 * parts, elle ne porte que le gain À VENIR : celui déjà acquis au jour de
 * l'apport reste le vôtre, en report d'imposition, et vous le paierez le
 * jour où vous vendrez les parts de la holding, ou plus tôt si la condition
 * de réinvestissement n'est pas tenue.
 *
 * @param prix         le prix de cession de vos titres
 * @param acquisition  ce que vous les avez payés : le capital apporté, le plus
 *                     souvent
 * @param valeurApport la valeur de vos parts le jour où vous les apportez à la
 *                     holding ; absente si la holding détient la société dès
 *                     sa création
 * @param deuxAns      la holding détiendra les titres depuis au moins deux ans
 *                     le jour de la vente : sinon la plus-value est à court
 *                     terme, imposée en entier au taux de l'impôt sur les
 *                     sociétés
 */
export function revendre({ prix, acquisition = 0, valeurApport = null, deuxAns = true }) {
    const p = positif(prix);
    const plusValue = positif(p - acquisition);
    const sansImpot = plusValue * (PFU_IR + PFU_SOCIAL);

    const base = valeurApport === null ? acquisition : valeurApport;
    const plusValueHolding = positif(p - base);
    const quotePart = deuxAns ? plusValueHolding * QUOTE_PART_PLUS_VALUE : plusValueHolding;
    const impotHoldingVente = impotSocietes(quotePart);
    // Le gain acquis avant l'apport : le vôtre, en report, au taux de l'année
    // de l'apport.
    const plusValueReportee = valeurApport === null ? 0 : positif(valeurApport - acquisition);
    const impotReporte = plusValueReportee * (PFU_IR + PFU_SOCIAL);

    return {
        plusValue,
        sans: { impot: sansImpot, net: p - sansImpot },
        avec: {
            plusValue: plusValueHolding,
            quotePart,
            impot: impotHoldingVente,
            net: p - impotHoldingVente,
            plusValueReportee,
            impotReporte,
        },
        // Ce que la holding évite de payer le jour de la vente ; l'impôt
        // reporté reste dû plus tard.
        gain: sansImpot - impotHoldingVente - impotReporte,
        gainImmediat: sansImpot - impotHoldingVente,
    };
}
